import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';

export const useNonprofitAffiliationLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'Nonprofit Affiliation',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION) || 'NO',
      [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS) || '',
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION]: z
          .string()
          .min(1, { message: tValidation('validation.required') }),
        [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data[EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION] === 'YES') {
          const details = data[EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS];

          if (!details || details.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.required'),
              path: [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS],
            });
          } else if (details.length < 30) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.min30chars'),
              path: [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS],
            });
          } else if (details.length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.max100chars'),
              path: [EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    const affiliationValue = formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION);
    const detailsValue = formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS);

    // Save main question
    saveEddQuestionToLocalStore({
      question: t('organic.nonprofitAffiliation.title'),
      questionId: EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION,
      answer: JSON.stringify(affiliationValue),
    });

    // Save details if YES was selected and details provided
    if (affiliationValue === 'YES' && detailsValue && detailsValue.trim() !== '') {
      saveEddQuestionToLocalStore({
        question: t('organic.nonprofitAffiliation.detailsPrompt'),
        questionId: EddEmbeddedFields.EDD_NONPROFIT_AFFILIATION_DETAILS,
        answer: JSON.stringify(detailsValue),
      });
    }

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_NONPROFIT_AFFILIATION);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
